import { IViolation } from '../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { sanitizeAndFormatString, stringIsNullOrEmpty } from 'app/shared/utils/string.util';

import { DatasetRegistryService } from 'app/registries/dataset/dataset-registry.service';
import { TabularTypeDataset, tabularDatasetValidators, ModelConstants, Constants, IValidationContext, format, isLogicAppExpression } from 'shell-models-lib';
import { CopyActivity } from '../pipeline/copy.activity';
import { AzureDataExplorerCopySource } from '../pipeline/copysource/azure-data-explorer.copysource';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { isTableNameRequired } from 'app/shared/utils/dataset-utils';
import { LakehouseTableCopySource } from '../pipeline/copysource/lakehouse-table.copysource';
import { LookupActivity } from '../pipeline/lookup.activity';
import { HubSpotLinkedService } from '../linkedservice/hub-spot.linkedservice';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';

tabularDatasetValidators.validateTableName = async function validateTableName(dataset: TabularTypeDataset, value?: string, violation?: IViolation, context?: IValidationContext): Promise<IViolation[] | undefined> {
    if (dataset != null) {
        const datasetRegistry = (await getServiceInstance<DatasetRegistryService>(context.contextData, 'DatasetRegistryService'));
        const model = Metamodel.modelOf(dataset);
        const copysource = (dataset.owner as CopyActivity)?.source as AzureDataExplorerCopySource;
        const datasetConfig = datasetRegistry.getDatasetConfiguration(model.name);
        if (stringIsNullOrEmpty(value) &&
            (
                isTableNameRequired(datasetConfig, dataset) ||
                (
                    (model.name === ModelConstants.azureDataExplorerDataset || model.name === ModelConstants.kustoDatabaseDataset) &&
                    __IS_FABRIC_DPE__ &&
                    copysource?.mode === 'table'
                )
            )
        ) {
            return [
                {
                    messageType: 'missing',
                    message: () => sanitizeAndFormatString(stringResources.RequiredTemplate, datasetConfig.tableNameDisplay || stringResources.ModelName_Table)
                }
            ];
        }
    }
    return undefined;
};

tabularDatasetValidators.validateTable = async function validateTableName(dataset: TabularTypeDataset, value?: string, violation?: IViolation, context?: IValidationContext): Promise<IViolation[] | undefined> {
    if (dataset != null) {
        const model = Metamodel.modelOf(dataset);
        const datasetOwner = dataset.owner;
        if (
            model.name === ModelConstants.lakehouseTableDataset &&
            (
                datasetOwner == null ||
                !(datasetOwner instanceof CopyActivity || datasetOwner instanceof LookupActivity) ||
                dataset === (datasetOwner as CopyActivity).sinkDataset ||
                (datasetOwner.source as LakehouseTableCopySource).mode === 'table'
            ) &&
            !isLogicAppExpression(value) &&
            !Constants.lakehouseTableNameValidation.test(value)
        ) {
            return [
                {
                    message: () => stringResources.Validator_Lakehouse_TableName
                }
            ];
        }
        
        // HubSpot v2 table field required validation (only for split path datasets)
        if (model.name === adfModelTypeMap.hubspot.datasetType) {
            const datasetRegistry = await getServiceInstance<DatasetRegistryService>(context.contextData, 'DatasetRegistryService');
            const datasetConfig = datasetRegistry.getDatasetConfiguration(model.name);
            const linkedService = dataset.linkedService as HubSpotLinkedService;
            
            // Only validate table field if this dataset is using split path format
            // (existing datasets with tableName should be validated via isTableNameRequired)
            const isUsingSplitPath = stringIsNullOrEmpty(dataset.tableName) && 
                                      stringIsNullOrEmpty(TabularTypeDataset.TABLE_NAME.templateValue(dataset));
            
            if (datasetConfig.isTableRequired === true &&
                linkedService?.version === 'v2' &&
                isUsingSplitPath &&
                stringIsNullOrEmpty(value) &&
                stringIsNullOrEmpty(TabularTypeDataset.TABLE.templateValue(dataset))
            ) {
                return [
                    {
                        messageType: 'missing',
                        message: () => sanitizeAndFormatString(stringResources.RequiredTemplate, stringResources.ModelName_Table)
                    }
                ];
            }
        }
    }
    return undefined;
};

tabularDatasetValidators.validateSchema = async function validateTableName(dataset: TabularTypeDataset, value?: string, violation?: IViolation, context?: IValidationContext): Promise<IViolation[] | undefined> {
    if (dataset != null) {
        const model = Metamodel.modelOf(dataset);
        if (model.name === ModelConstants.lakehouseTableDataset && !isLogicAppExpression(value) && !Constants.lakehouseSchemaValidation.test(value)) {
            return [
                {
                    message: () => format(stringResources.Validator_NamePattern_All_AlphanumericUnderscore, 'Schema')
                }
            ];
        }
    }
    return undefined;
};

export { TabularTypeDataset };
